import { useEffect, useRef } from "react";
import { useGoogleMap } from "@react-google-maps/api";

/*
  Drop-in replacement for google.maps.drawing.DrawingManager, which was removed in
  Maps JavaScript API v3.65. Must be rendered inside <GoogleMap>.

  tool "polygon":   click to add points; double-click or click the first point to finish;
                    right-click removes the last point.
  tool "rectangle": click one corner, move, click the opposite corner.
  tool null:        no drawing (the finished shape stays on the map and is editable).

  The finished shape is reported through the same callbacks GeoMap used with
  DrawingManager: onPolygonComplete(points[{lat, lng}]) and onRectangleComplete(bounds).
*/
const BRAND = "#6e00dc";
const SHAPE_STYLE = {
  strokeColor: BRAND,
  strokeWeight: 2,
  fillColor: BRAND,
  fillOpacity: 0.18,
  zIndex: 1,
};

const ShapeDrawer = ({ tool, clearToken, onPolygonComplete, onRectangleComplete, onToolChange }) => {
  const map = useGoogleMap();
  const shapeRef = useRef(null);
  const shapeListenersRef = useRef([]);
  const callbacksRef = useRef({});
  callbacksRef.current = { onPolygonComplete, onRectangleComplete, onToolChange };

  const removeShape = () => {
    shapeListenersRef.current.forEach((l) => l?.remove());
    shapeListenersRef.current = [];
    if (shapeRef.current) {
      shapeRef.current.setMap(null);
      shapeRef.current = null;
    }
  };

  // "Clear" button
  const firstClearRender = useRef(true);
  useEffect(() => {
    if (firstClearRender.current) {
      firstClearRender.current = false;
      return;
    }
    removeShape();
  }, [clearToken]);

  // remove the committed shape when the component unmounts
  useEffect(() => removeShape, []);

  useEffect(() => {
    if (!map || !tool) return undefined;
    const g = window.google.maps;
    const listeners = [];
    const temp = [];

    const cleanup = () => {
      listeners.forEach((l) => l?.remove());
      temp.forEach((o) => o.setMap(null));
    };

    const commitPolygon = (latLngs) => {
      removeShape();
      // plain {lat, lng} objects, the same shape the edit handler reports
      const points = latLngs.map((p) => ({ lat: p.lat(), lng: p.lng() }));
      const polygon = new g.Polygon({ map, paths: points, editable: true, ...SHAPE_STYLE });
      shapeRef.current = polygon;
      const emit = () => {
        const next = polygon
          .getPath()
          .getArray()
          .map((p) => ({ lat: p.lat(), lng: p.lng() }));
        callbacksRef.current.onPolygonComplete?.(next);
      };
      const path = polygon.getPath();
      shapeListenersRef.current = [
        g.event.addListener(path, "set_at", emit),
        g.event.addListener(path, "insert_at", emit),
        g.event.addListener(path, "remove_at", emit),
      ];
      callbacksRef.current.onPolygonComplete?.(points);
    };

    const commitRectangle = (bounds) => {
      removeShape();
      const rectangle = new g.Rectangle({ map, bounds, editable: true, ...SHAPE_STYLE });
      shapeRef.current = rectangle;
      shapeListenersRef.current = [
        g.event.addListener(rectangle, "bounds_changed", () =>
          callbacksRef.current.onRectangleComplete?.(rectangle.getBounds())
        ),
      ];
      callbacksRef.current.onRectangleComplete?.(bounds);
    };

    // ~2px at the current zoom, used to ignore the duplicate click a double-click produces
    const sameSpot = (a, b) => {
      const eps = (360 / (256 * 2 ** map.getZoom())) * 2;
      return Math.abs(a.lat() - b.lat()) < eps && Math.abs(a.lng() - b.lng()) < eps;
    };

    if (tool === "polygon") {
      const points = [];
      const line = new g.Polyline({
        map,
        path: [],
        clickable: false,
        strokeColor: BRAND,
        strokeWeight: 2,
        zIndex: 3,
      });
      const preview = new g.Polyline({
        map,
        path: [],
        clickable: false,
        strokeOpacity: 0,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 0.7, strokeColor: BRAND, scale: 3 },
            offset: "0",
            repeat: "12px",
          },
        ],
        zIndex: 3,
      });
      const firstPoint = new g.Marker({
        clickable: true,
        visible: false,
        zIndex: 4,
        title: "Click to close the shape",
        icon: {
          path: g.SymbolPath.CIRCLE,
          scale: 7,
          fillColor: "#fff",
          fillOpacity: 1,
          strokeColor: BRAND,
          strokeWeight: 3,
        },
      });
      temp.push(line, preview, firstPoint);

      const finish = () => {
        if (points.length < 3) return;
        const done = points.slice();
        cleanup();
        commitPolygon(done);
        callbacksRef.current.onToolChange?.(null);
      };

      listeners.push(
        map.addListener("click", (e) => {
          const last = points[points.length - 1];
          if (last && sameSpot(last, e.latLng)) return;
          points.push(e.latLng);
          line.setPath(points);
          if (points.length === 1) {
            firstPoint.setPosition(e.latLng);
            firstPoint.setMap(map);
            firstPoint.setVisible(true);
          }
        }),
        map.addListener("mousemove", (e) => {
          const last = points[points.length - 1];
          preview.setPath(last ? [last, e.latLng] : []);
        }),
        map.addListener("rightclick", () => {
          points.pop();
          line.setPath(points);
          preview.setPath([]);
          if (!points.length) firstPoint.setVisible(false);
        }),
        map.addListener("dblclick", finish),
        g.event.addListener(firstPoint, "click", finish)
      );
    } else if (tool === "rectangle") {
      let corner = null;
      const preview = new g.Rectangle({ map, clickable: false, visible: false, ...SHAPE_STYLE, fillOpacity: 0.1 });
      temp.push(preview);

      const boundsOf = (a, b) =>
        new g.LatLngBounds(
          { lat: Math.min(a.lat(), b.lat()), lng: Math.min(a.lng(), b.lng()) },
          { lat: Math.max(a.lat(), b.lat()), lng: Math.max(a.lng(), b.lng()) }
        );

      listeners.push(
        map.addListener("click", (e) => {
          if (!corner) {
            corner = e.latLng;
            return;
          }
          if (sameSpot(corner, e.latLng)) return;
          const bounds = boundsOf(corner, e.latLng);
          cleanup();
          commitRectangle(bounds);
          callbacksRef.current.onToolChange?.(null);
        }),
        map.addListener("mousemove", (e) => {
          if (!corner) return;
          preview.setBounds(boundsOf(corner, e.latLng));
          preview.setVisible(true);
        }),
        map.addListener("rightclick", () => {
          corner = null;
          preview.setVisible(false);
        })
      );
    }

    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, tool]);

  return null;
};

export default ShapeDrawer;
